import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import { useCompany } from "../../components/context/CompanyContext";
import { userRequest } from "../../requestMethods";
import { formatPricing } from "../../util/helper";
import { effectiveStories } from "../../util/storeScope";
import { screenRoute } from "../../util/accessControl";
import "./report.css";
import { titleSetRef } from "../../redux/titleRedux";
import Loading from "../../components/loading/loading";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import InventoryIcon from "@mui/icons-material/Inventory";
import PaymentsIcon from "@mui/icons-material/Payments";

const BRAND = "#EE8253";

const PILLAR_COLORS = {
  vendas:     { from: "#EE8253", to: "#E8502A" },
  clientes:   { from: "#3B82F6", to: "#1D4ED8" },
  produtos:   { from: "#10B981", to: "#059669" },
  pagamentos: { from: "#8B5CF6", to: "#6D28D9" },
};

function PillarCard({ pillar, icon, title, period, heroValue, heroSub, children, onDetails }) {
  const c = PILLAR_COLORS[pillar];
  const [hover, setHover] = useState(false);
  return (
    <div style={{ background: "#fff", borderRadius: 14, overflow: "hidden", border: "1px solid #E5E7EB", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}>
      {/* Header colorido */}
      <div style={{ background: `linear-gradient(135deg, ${c.from}, ${c.to})`, padding: "24px 28px 22px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "rgba(255,255,255,0.9)", display: "flex" }}>{icon}</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "Manrope" }}>{title}</span>
          </div>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", fontFamily: "Manrope" }}>{period}</span>
        </div>
        <div style={{ fontSize: 38, fontWeight: 900, color: "#fff", lineHeight: 1.1, fontFamily: "Manrope", letterSpacing: "-0.02em" }}>{heroValue}</div>
        {heroSub && <div style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", marginTop: 6, fontFamily: "Manrope" }}>{heroSub}</div>}
      </div>
      {/* Body */}
      <div style={{ padding: "4px 0 0", flex: 1 }}>{children}</div>
      {/* Rodapé: Ver mais detalhes */}
      {onDetails && (
        <div
          onClick={onDetails}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            padding: "12px",
            marginTop: 4,
            cursor: "pointer",
            borderTop: "1px solid #F3F4F6",
            color: c.from,
            fontSize: 13,
            fontWeight: 700,
            fontFamily: "Manrope",
            background: hover ? "#F9FAFB" : "transparent",
            transition: "background 0.15s",
          }}
        >
          Ver mais detalhes <span style={{ fontSize: 15 }}>›</span>
        </div>
      )}
    </div>
  );
}

function MetricRow({ label, value, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "13px 28px",
        borderBottom: "1px solid #F3F4F6",
        cursor: onClick ? "pointer" : "default",
        transition: "background 0.15s",
        background: hover && onClick ? "#F9FAFB" : "transparent",
      }}
      onMouseEnter={() => onClick && setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      <span style={{ fontSize: 15, color: "#6B7280", fontFamily: "Manrope" }}>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>{value}</span>
        {onClick && <span style={{ fontSize: 16, color: "#D1D5DB" }}>›</span>}
      </div>
    </div>
  );
}


export default function HomeV2() {
  const [orders, setOrders] = useState();
  const [paymentMix, setPaymentMix] = useState([]);
  const [prdBestSeller, setPrdBestSeller] = useState(null);
  const [customers, setCustomers] = useState();
  const [stock, setStock] = useState();
  const [carts, setCarts] = useState();
  const [prdWithoutOrder, setPrdWithoutOrder] = useState([]);
  const [dataIni, setDateIni] = useState(() => dayjs().add(-7, "day").format("YYYY-MM-DD"));
  const [dataFim, setDateFim] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [readonly, setReadonly] = useState("7");
  const [isLoading, setIsLoading] = useState(true);
  const [stories, setStories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [storeIds, setStoreIds] = useState([]);

  const currentUser = useSelector((state) => state.user.currentUser);
  const { companyId, slug, accessControl } = useCompany();
  const nav = useNavigate();
  const dispatch = useDispatch();
  // "detalhes" de cada pilar abre o primeiro card permitido daquele dash e
  // some quando o perfil não acessa nenhum.
  const detailsTo = (key) => {
    const route = screenRoute(accessControl, currentUser?.position, key);
    return route ? () => nav(`/${slug}/${route}`) : undefined;
  };

  useEffect(() => {
    dispatch(titleSetRef({ title: "Home" }));
    if (!currentUser) nav("../login");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (companyId > 0 && currentUser) getStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    setStoreIds(selected.map((s) => s.value));
  }, [selected]);

  useEffect(() => {
    if (companyId > 0 && currentUser) {
      setIsLoading(true);
      carregarTudo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dataIni, dataFim, storeIds, currentUser]);

  const getStories = async () => {
    try {
      const res = await userRequest.get(`${companyId}/store/stories`);
      const list = res.data.map((item) => ({ label: item.store, value: item.storeId }));
      setStories(list);
      if (currentUser?.position === "Gerente") {
        const userStore = list.find((s) => s.value === currentUser.storeId);
        if (userStore) setSelected([userStore]);
      }
    } catch {}
  };

  async function carregarTudo() {
    try {
      // Primeira onda: dados de pedidos + clientes + carrinhos (paralelo)
      await Promise.all([
        getOrders(),
        getCustomers(),
        getCarts(),
      ]);

      // Segunda onda: dados de produtos (paralelo)
      await Promise.all([
        getStock(),
        getWithoutOrder(),
      ]);
    } finally {
      setIsLoading(false);
    }
  }

  const buildBody = () => ({
    start: dataIni,
    end: dataFim,
    stories: effectiveStories(currentUser, storeIds),
  });

  // Carrinhos filtrados por data → Valores em Aberto (idêntico à home antiga)
  const getCarts = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportCarts/carts`, buildBody());
      setCarts(res.data);
    } catch {}
  };

  const getCustomers = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportCustomer/customers`, buildBody());
      setCustomers(res.data);
    } catch {}
  };

  const getOrders = async () => {
    try {
      const res = await userRequest.post(`${companyId}/orders/find`, {
        page: 0,
        limit: 10000,
        paginated: true,
        filters: [
          { field: "createdAt", operator: "greaterThanOrEqual", value: dataIni },
          { field: "createdAt", operator: "lessThanOrEqual", value: dataFim },
        ],
        stories: effectiveStories(currentUser, storeIds),
      });
      const list = res.data.rows || [];
      const byStatus = (status) => list.filter((o) => o.status === status);
      const finalizados = byStatus("Finalizado");

      // Agrega contadores e valores
      setOrders({
        cTotal:      list.length,
        cFinalizado: finalizados.length,
        cPendente:   byStatus("Pendente").length,
        aFinalizado: finalizados.reduce((s, o) => s + (o.valorLiquido || 0), 0),
        aPendente:   byStatus("Pendente").reduce((s, o) => s + (o.valorLiquido || 0), 0),
        aTotal:      list.reduce((s, o) => s + (o.valorLiquido || 0), 0),
      });

      // Mix de pagamentos: agrupa por label, coletando TODOS os ids distintos de
      // cada tipo (um mesmo label pode ter ids diferentes entre pedidos — padrão
      // companyId 0 vs específico). Assim o filtro pega todos os pedidos do tipo.
      const mixMap = {};
      let semTipoValor = 0;
      finalizados.forEach((o) => {
        const label = o.paymentType;
        if (label) {
          if (!mixMap[label]) mixMap[label] = { value: 0, ids: new Set() };
          mixMap[label].value += o.valorLiquido || 0;
          if (o.paymentTypeId) mixMap[label].ids.add(o.paymentTypeId);
        } else {
          semTipoValor += o.valorLiquido || 0;
        }
      });
      const ordenado = Object.entries(mixMap)
        .map(([name, d]) => ({ name, value: d.value, paymentTypeIds: [...d.ids] }))
        .filter((d) => d.value > 0)
        .sort((a, b) => b.value - a.value);

      // Mostra os 3 maiores; soma o restante + sem-tipo em um bucket consolidado
      const TOP = 3;
      const principais = ordenado.slice(0, TOP);
      const restantes = ordenado.slice(TOP);
      const outrosValor =
        restantes.reduce((s, d) => s + d.value, 0) + semTipoValor;
      if (outrosValor > 0) {
        // Evita colisão caso exista um tipo de pagamento real chamado "Outros"
        const jaTemOutros = principais.some((d) => d.name === "Outros");
        principais.push({
          name: jaTemOutros ? "Demais" : "Outros",
          value: outrosValor,
          // todos os ids distintos dos tipos consolidados
          paymentTypeIds: restantes.flatMap((d) => d.paymentTypeIds),
        });
      }
      setPaymentMix(principais);

      // Best seller: produto com mais unidades vendidas no período
      const prdMap = {};
      list.forEach((o) => {
        (o.products || []).forEach((p) => {
          if (p.ref) prdMap[p.ref] = (prdMap[p.ref] || 0) + (p.quantity || 1);
        });
      });
      const sorted = Object.entries(prdMap).sort((a, b) => b[1] - a[1]);
      setPrdBestSeller(sorted.length > 0 ? { ref: sorted[0][0], count: sorted[0][1] } : null);

    } catch {}
  };

  const getStock = async () => {
    try {
      const res = await userRequest.get(`${companyId}/reportProducts/prodstock`);
      setStock(res.data);
    } catch {}
  };

  const getWithoutOrder = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportProducts/withoutOrder`, buildBody());
      setPrdWithoutOrder(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  const handleChangeStart = (e) => setDateIni(e.target.value);
  const handleChangeEnd = (e) => setDateFim(e.target.value);
  const handleChange = (e) => {
    const v = e.target.value;
    setReadonly(v);
    switch (v) {
      case "0":
        setDateIni(dayjs().format("YYYY-MM-DD"));
        setDateFim(dayjs().format("YYYY-MM-DD"));
        break;
      case "7":
        setDateIni(dayjs().add(-7, "day").format("YYYY-MM-DD"));
        setDateFim(dayjs().format("YYYY-MM-DD"));
        break;
      case "15":
        setDateIni(dayjs().add(-15, "day").format("YYYY-MM-DD"));
        setDateFim(dayjs().format("YYYY-MM-DD"));
        break;
      case "30":
        setDateIni(dayjs().add(-30, "day").format("YYYY-MM-DD"));
        setDateFim(dayjs().format("YYYY-MM-DD"));
        break;
      default:
        break;
    }
  };

  const periodLabel =
    readonly === "F"
      ? "filtro personalizado"
      : readonly === "0"
      ? "hoje"
      : `últimos ${readonly} dias`;

  const faturamento = orders?.aFinalizado || 0;
  const totalPedidos = orders?.cTotal || 0;
  const pedidosFinalizados = orders?.cFinalizado || 0;
  const pedidosPendentes = orders?.cPendente || 0;
  const stockOverCount = stock?.stockOver || 0;
  const invisComEstoque = (stock?.completedVisible || 0) + (stock?.incompletedVisible || 0);

  const paymentData = paymentMix;


  return (
    <div style={{ padding: "16px 16px 48px 4px", fontFamily: "Manrope", background: "#F9FAFB", minHeight: "100vh" }}>

      {/* Header + Filtro */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Indicadores</span>
          <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8 }}>{periodLabel}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>

          {/* Pills Lojas */}
          {stories.length > 0 && (() => {
            const isGerente = currentUser?.position === "Gerente";
            return (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Lojas</div>
                <div style={{ display: "flex", gap: 6, border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
                  <label style={{ cursor: isGerente ? "default" : "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${selected.length === 0 ? BRAND : "#E5E7EB"}`, background: selected.length === 0 ? BRAND : "transparent", color: selected.length === 0 ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600 }}
                    onClick={() => !isGerente && setSelected([])}>Todas</label>
                  {stories.map((story) => {
                    const isActive = selected.some((s) => s.value === story.value);
                    return (
                      <label key={story.value}
                        style={{ cursor: isGerente ? "default" : "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${isActive ? BRAND : "#E5E7EB"}`, background: isActive ? BRAND : "transparent", color: isActive ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600, opacity: isGerente ? 0.7 : 1 }}
                        onClick={() => { if (isGerente) return; setSelected(isActive ? selected.filter((s) => s.value !== story.value) : [...selected, story]); }}>
                        {story.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Pills Período */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Período</div>
            <div style={{ display: "flex", gap: 6, border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
              {[{ v: "30", l: "30 dias" }, { v: "15", l: "15 dias" }, { v: "7", l: "7 dias" }, { v: "0", l: "Hoje" }, { v: "F", l: "Filtro" }].map(({ v, l }) => (
                <label key={v} style={{ cursor: "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${readonly === v ? BRAND : "#E5E7EB"}`, background: readonly === v ? BRAND : "transparent", color: readonly === v ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600 }}>
                  <input type="radio" value={v} checked={readonly === v} onChange={handleChange} style={{ display: "none" }} />
                  {l}
                </label>
              ))}
              {readonly === "F" && (
                <>
                  <div style={{ width: 1, background: "#E5E7EB", margin: "2px 2px" }} />
                  <input type="date" value={dataIni} onChange={handleChangeStart} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                  <span style={{ color: "#9CA3AF", fontSize: 12 }}>—</span>
                  <input type="date" value={dataFim} onChange={handleChangeEnd} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* 4 Pilares — 2x2 */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>

            {/* ── VENDAS ── */}
            <PillarCard
              pillar="vendas"
              icon={<TrendingUpIcon style={{ fontSize: 18 }} />}
              title="Vendas"
              period={periodLabel}
              heroValue={formatPricing(orders?.aFinalizado || 0)}
              heroSub={`${pedidosFinalizados} pedidos finalizados`}
              onDetails={detailsTo("Dashboard/Vendas")}
            >
              <MetricRow label={`Carrinho (${carts?.quantity || 0})`} value={formatPricing(carts?.total || 0)} onClick={() => nav(`/${slug}/cartlist`)} />
              <MetricRow label={`Pedidos finalizados (${pedidosFinalizados})`} value={formatPricing(orders?.aFinalizado || 0)} onClick={() => nav(`/${slug}/orders?start=${dataIni}&end=${dataFim}&status=Finalizado&from=homev2`)} />
              <MetricRow label={`Pedidos pendentes (${pedidosPendentes})`} value={formatPricing(orders?.aPendente || 0)} onClick={() => nav(`/${slug}/orders?start=${dataIni}&end=${dataFim}&status=Pendente&from=homev2`)} />
              <MetricRow label={`Total pedidos (${totalPedidos})`} value={formatPricing(orders?.aTotal || 0)} onClick={() => nav(`/${slug}/orders?start=${dataIni}&end=${dataFim}&from=homev2`)} />
            </PillarCard>

            {/* ── CLIENTES ── */}
            <PillarCard
              pillar="clientes"
              icon={<PeopleIcon style={{ fontSize: 18 }} />}
              title="Clientes"
              period={periodLabel}
              heroValue={customers?.qtdeCustomer || 0}
              heroSub={`${customers?.newCustomer || 0} novos no período`}
              onDetails={detailsTo("Dashboard/Clientes")}
            >
              <MetricRow label="Novos clientes" value={customers?.newCustomer || 0} onClick={() => nav(`/${slug}/customers?start=${dataIni}&end=${dataFim}&enabled=Aprovado&from=homev2`)} />
              <MetricRow label="Acesso aos catálogos" value={`${customers?.acess || 0}%`} onClick={() => nav(`/${slug}/customers?acessStart=${dataIni}&acessEnd=${dataFim}&enabled=Aprovado&from=homev2`)} />
              <MetricRow label="Qtde clientes" value={customers?.qtdeCustomer || 0} onClick={() => nav(`/${slug}/customers?enabled=Aprovado&from=homev2`)} />
              <MetricRow label="Clientes ativos (90 dias)" value={`${customers?.ativos || 0}%`} onClick={() => nav(`/${slug}/customers?activeLast90=true&enabled=Aprovado&from=homev2`)} />
            </PillarCard>

            {/* ── PRODUTOS ── */}
            <PillarCard
              pillar="produtos"
              icon={<InventoryIcon style={{ fontSize: 18 }} />}
              title="Produtos"
              period={periodLabel}
              heroValue={prdBestSeller?.ref || "-"}
              heroSub={`Mais vendido · ${prdBestSeller?.count || 0}x no período`}
              onDetails={detailsTo("Dashboard/Produtos")}
            >
              <MetricRow label={`Mais vendidos (${prdBestSeller?.count || 0}x)*`} value={prdBestSeller?.ref || "-"} onClick={() => nav(`/${slug}/products?start=${dataIni}&end=${dataFim}&from=homev2`)} />
              <MetricRow label="Sem venda*" value={prdWithoutOrder.length} onClick={() => nav(`/${slug}/products?noSales=1&start=${dataIni}&end=${dataFim}&from=homev2`)} />
              <MetricRow label="Visível sem estoque" value={stockOverCount} onClick={() => nav(`/${slug}/products?stockFilter=visibleNoStock&from=homev2`)} />
              <MetricRow label="Invisível com estoque" value={invisComEstoque} onClick={() => nav(`/${slug}/products?stockFilter=invisibleWithStock&from=homev2`)} />
            </PillarCard>

            {/* ── PAGAMENTOS ── */}
            <PillarCard
              pillar="pagamentos"
              icon={<PaymentsIcon style={{ fontSize: 18 }} />}
              title="Pagamentos"
              period={periodLabel}
              heroValue={formatPricing(paymentData.reduce((s, d) => s + d.value, 0))}
              heroSub={`${paymentData.length} método${paymentData.length !== 1 ? "s" : ""} utilizados`}
              onDetails={detailsTo("Dashboard/Vendas/Pagamentos")}
            >
              {paymentData.length === 0 ? (
                <div style={{ padding: "20px 24px", color: "#9CA3AF", fontSize: 13, textAlign: "center" }}>Sem dados no período</div>
              ) : (
                paymentData.map((d, i) => (
                  <MetricRow
                    key={i}
                    label={d.name}
                    value={formatPricing(d.value)}
                    onClick={
                      d.paymentTypeIds && d.paymentTypeIds.length > 0
                        ? () => nav(`/${slug}/orders?start=${dataIni}&end=${dataFim}&status=Finalizado&paymentTypeIds=${d.paymentTypeIds.join(",")}&from=homev2`)
                        : undefined
                    }
                  />
                ))
              )}
            </PillarCard>

          </div>
        </div>
      )}
    </div>
  );
}
